import { useEffect, useState } from 'react'

export interface UseMediaQueryOptions {
  /**
     * As `window.matchMedia()` is unavailable on the server,
     * it returns a default matches during the first mount.
     * @default false
     */
  defaultMatches?: boolean
  /**
     * You can provide your own implementation of matchMedia.
     * This can be used for handling an iframe content window.
     */
  matchMedia?: typeof window.matchMedia
  /**
     * To perform the server-side hydration, the hook needs to render twice.
     * A first time with `defaultMatches`, the value of the server, and a second time with the resolved value.
     * This double pass rendering cycle comes with a drawback: it's slower.
     * You can set this option to `true` if you use the returned value **only** client-side.
     * @default false
     */
  noSsr?: boolean
  /**
     * You can provide your own implementation of `matchMedia`, it's used when rendering server-side.
     */
  ssrMatchMedia?: (query: string) => { matches: boolean }
}

export function useMediaQuery(
  query: string,
): boolean {
  const supportMatchMedia = typeof window?.matchMedia !== 'undefined'
  const matchMedia = supportMatchMedia ? window.matchMedia : null
  const [match, setMatch] = useState(() => {
    if (matchMedia) {
      return matchMedia(query).matches
    }
  })

  useEffect(() => {
    let active = true

    if (!matchMedia) {
      return undefined
    }

    const queryList = matchMedia(query)
    const updateMatch = () => {
      // Workaround Safari wrong implementation of matchMedia
      // TODO can we remove it?
      // https://github.com/mui/material-ui/pull/17315#issuecomment-528286677
      if (active) {
        setMatch(queryList.matches)
      }
    }
    updateMatch()
    // TODO: Use `addEventListener` once support for Safari < 14 is dropped
    queryList.addListener(updateMatch)
    return () => {
      active = false
      queryList.removeListener(updateMatch)
    }
  }, [query, matchMedia])

  return match || false
}
